/* eslint-disable */
import React, { useState, useEffect } from 'react';
import { Users, Building2, Target, UserX } from 'lucide-react';
import { useSelector } from 'react-redux';
import Page from '../../../components/Page';
import { getDashboardStats } from '../../../services/clientAdmin/dashboardServices';

function ProgressBar({ value, max = 100, color = 'bg-purple-500' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
      <div className={`h-2 ${color} rounded-full`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function ManagerDashboard() {
  const { userInfo } = useSelector((state) => state);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    activeLearners: 0, departments: 0, teams: 0, learnersInTeams: 0,
    topPerformers: [], atRiskLearners: [], departmentPerformance: [],
  });

  useEffect(() => {
    setLoading(true);
    getDashboardStats().then(res => {
      if (res.ok) setData(res.data);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Page title="Dashboard">
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="mb-6"><div className="h-6 w-32 bg-gray-200 rounded animate-pulse" /><div className="h-4 w-48 bg-gray-100 rounded animate-pulse mt-2" /></div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[1,2,3,4].map(i => <div key={i} className="bg-white rounded-xl p-4 border border-gray-100"><div className="h-10 bg-gray-100 rounded animate-pulse" /></div>)}
        </div>
        <div className="bg-white rounded-xl p-5 border border-gray-100 mb-5"><div className="h-4 w-40 bg-gray-200 rounded animate-pulse mb-3" />{[1,2,3].map(i => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse mb-2" />)}</div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100">{[1,2,3].map(i => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse mb-2" />)}</div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">{[1,2,3].map(i => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse mb-2" />)}</div>
        </div>
      </div>
    </Page>
  );

  return (
    <Page title="Dashboard">
      <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Your departments overview</p>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[
            { l: 'Learners', v: data.activeLearners, icon: Users, c: 'bg-blue-50 text-blue-600' },
            { l: 'Departments', v: data.departments, icon: Building2, c: 'bg-purple-50 text-purple-600' },
            { l: 'Teams', v: data.teams, icon: Target, c: 'bg-green-50 text-green-600' },
            { l: 'In Teams', v: data.learnersInTeams, icon: Users, c: 'bg-amber-50 text-amber-600' },
          ].map((k, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
              <div className="flex items-center gap-3">
                <div className={`${k.c} rounded-lg p-2.5`}><k.icon size={18} /></div>
                <div><p className="text-xl font-bold text-gray-900">{k.v}</p><p className="text-xs text-gray-500">{k.l}</p></div>
              </div>
            </div>
          ))}
        </div>

        {/* Department Performance */}
        {data.departmentPerformance?.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 mb-5">
            <p className="text-sm font-semibold text-gray-700 mb-3">Department Performance</p>
            <div className="space-y-3">
              {data.departmentPerformance.map((d, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between mb-1">
                      <span className="text-sm text-gray-800 font-medium truncate">{d.name}</span>
                      <span className="text-sm font-semibold text-gray-900">{d.avgScore || 0}%</span>
                    </div>
                    <ProgressBar value={d.avgScore || 0} color={d.avgScore >= 70 ? 'bg-green-500' : d.avgScore >= 50 ? 'bg-blue-500' : 'bg-orange-400'} />
                  </div>
                  <span className="text-xs text-gray-400 w-16 text-right">{d.learners} users</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Top Performers */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <p className="text-sm font-semibold text-gray-700 mb-3">Top Performers</p>
            {(!data.topPerformers || data.topPerformers.length === 0) ? <p className="text-xs text-gray-400">No assessment data yet</p> : (
              <div className="space-y-2">
                {data.topPerformers.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</div>
                    <div className="flex-1"><p className="text-sm font-medium text-gray-900">{p.firstName} {p.lastName}</p></div>
                    <p className="text-sm font-semibold">{p.avgScore}%</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* At-Risk Learners */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <p className="text-sm font-semibold text-gray-700 mb-3">At-Risk Learners</p>
            {(!data.atRiskLearners || data.atRiskLearners.length === 0) ? <p className="text-xs text-gray-400">No at-risk learners</p> : (
              <div className="space-y-2">
                {data.atRiskLearners.map((r, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-red-50/50 border border-red-100">
                    <UserX size={14} className="text-red-400" />
                    <div className="flex-1"><p className="text-sm text-gray-800">{r.firstName} {r.lastName}</p></div>
                    <span className="text-sm font-semibold text-red-600">{r.avgScore}%</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}
